import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PenSquare, Search, Sparkles, UserPlus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Contact } from "@lumika/shared";
import {
  Button,
  ChannelIcon,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  channelLabel,
  cn,
  toast,
} from "@lumika/ui";
import { bdcApi } from "../api/bdc";
import { telephonyApi } from "../api/telephony";
import { contactDisplayName } from "../lib/contact";
import {
  CallDialog,
  useVoiceCapabilities,
  useActiveOutboundChannels,
  collectContactPhones,
} from "./ContactOutreach";

/**
 * Unified "Start Conversation" dialog — extends the inbox "New message" dialog
 * to support SMS, email, and voice (AI-placed or operator softphone), with an
 * optional `presetContact` that skips straight to channel selection.
 *
 * When `presetContact` is supplied (Customer 360 / Contact view), the recipient
 * is fixed and the first step is skipped. Without it (Inbox entry), the
 * operator searches existing contacts or types a new phone/email.
 */

type Channel = "sms" | "email" | "voice";
type VoiceMode = "ai" | "operator";

type Recipient =
  | { kind: "contact"; contact: Contact }
  | { kind: "new"; channel: "sms" | "email"; value: string };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
/** E.164: leading +, country digit 1-9, then 7–14 more digits. */
const E164_RE = /^\+[1-9]\d{7,14}$/;
const normalizePhone = (v: string): string => v.replace(/[\s()-]/g, "");

export function ComposeDialog({
  presetContact,
  targetLeadId,
  targetRooftopId,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  trigger,
}: {
  /** When supplied, skip recipient selection and go straight to channel pick. */
  presetContact?: Contact;
  /**
   * Exact channel-less Lead created immediately before this dialog opened.
   * SMS/email bind and send on this row. Voice stays unavailable in this
   * targeted flow because its endpoint does not yet accept an exact Lead.
   */
  targetLeadId?: string;
  /** Loaded rooftop snapshot for `targetLeadId`; null means group-wide. */
  targetRooftopId?: string | null;
  /**
   * Controlled-open mode — set `open` + `onOpenChange` to drive the dialog
   * from external chrome (e.g. the topbar New menu).
   * When omitted, the component renders its own trigger button (default).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * `"none"` — suppresses the built-in trigger button so the dialog can be
   * opened exclusively from external chrome. Default: renders a button.
   */
  trigger?: "none";
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const voice = useVoiceCapabilities();
  const smsChannels = useActiveOutboundChannels("sms");
  const emailChannels = useActiveOutboundChannels("email");
  const compatibleChannels = (
    channels: Array<{ id: string; name: string; rooftopId: string | null }>,
  ) => {
    if (!targetLeadId) return channels;
    return channels
      .filter(
        (candidate) =>
          candidate.rooftopId === null || candidate.rooftopId === targetRooftopId,
      )
      .sort(
        (left, right) =>
          Number(right.rooftopId === targetRooftopId) -
          Number(left.rooftopId === targetRooftopId),
      );
  };
  const compatibleSmsChannels = compatibleChannels(smsChannels.channels);
  const compatibleEmailChannels = compatibleChannels(emailChannels.channels);

  // Support both controlled (openProp/onOpenChangeProp) and uncontrolled modes.
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? openProp : internalOpen;
  function setOpen(value: boolean) {
    if (isControlled) {
      onOpenChangeProp?.(value);
    } else {
      setInternalOpen(value);
    }
  }
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [recipient, setRecipient] = useState<Recipient | null>(
    presetContact ? { kind: "contact", contact: presetContact } : null,
  );
  const [channel, setChannel] = useState<Channel>("sms");
  const [voiceMode, setVoiceMode] = useState<VoiceMode>("ai");
  // Operator call dialog state — true after the user picks "Operator" and clicks Call.
  const [operatorCallOpen, setOperatorCallOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [notes, setNotes] = useState("");
  // Optional first/last for the "new" contact case.
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const results = useQuery({
    queryKey: ["bdc", "compose-contacts", debounced],
    queryFn: () =>
      bdcApi.contacts({ search: debounced, pageSize: 8, includeAnonymous: true }),
    enabled: open && !presetContact && recipient === null && debounced.length >= 2,
  });

  // Contact channels for voice phone resolution (preset only).
  const presetChannelsQuery = useQuery({
    queryKey: ["bdc", "contact-channels", presetContact?.id],
    queryFn: () => bdcApi.contactChannels(presetContact!.id),
    enabled: !!presetContact,
  });

  // A typed full email / E.164 phone that matches no contact can start a
  // brand-new conversation — the contact is created on send.
  const typedPhone = normalizePhone(debounced);
  const newTarget: (Recipient & { kind: "new" }) | null = EMAIL_RE.test(debounced)
    ? { kind: "new", channel: "email", value: debounced }
    : E164_RE.test(typedPhone)
      ? { kind: "new", channel: "sms", value: typedPhone }
      : null;

  // Available channels based on dealership config.
  const hasSms = compatibleSmsChannels.length > 0;
  const hasEmail = compatibleEmailChannels.length > 0;
  const hasVoice = voice.rooftops.length > 0;

  function reset(): void {
    setSearch("");
    setDebounced("");
    if (!presetContact) setRecipient(null);
    setChannel("sms");
    setVoiceMode("ai");
    setSubject("");
    setBody("");
    setNotes("");
    setNewFirstName("");
    setNewLastName("");
  }

  function chooseContact(contact: Contact): void {
    setRecipient({ kind: "contact", contact });
    setChannel(contact.primaryPhone && hasSms ? "sms" : "email");
  }

  function chooseNew(t: Recipient & { kind: "new" }): void {
    setRecipient(t);
    setChannel(t.channel);
  }

  // Phone numbers available to call for the current recipient.
  const recipientPhones: string[] =
    recipient?.kind === "contact"
      ? collectContactPhones(recipient.contact, presetChannelsQuery.data?.items)
      : recipient?.kind === "new" && recipient.channel === "sms"
        ? [recipient.value]
        : [];

  const firstPhone = recipientPhones[0] ?? null;

  const send = useMutation({
    mutationFn: async () => {
      // 1. Resolve (or create) the contact.
      let contactId: string;

      if (recipient?.kind === "contact") {
        contactId = recipient.contact.id;
      } else if (recipient?.kind === "new") {
        const resolved = await bdcApi.resolveOrCreateContact({
          ...(recipient.channel === "sms"
            ? { primaryPhone: recipient.value }
            : { primaryEmail: recipient.value }),
          ...(newFirstName.trim() ? { firstName: newFirstName.trim() } : {}),
          ...(newLastName.trim() ? { lastName: newLastName.trim() } : {}),
        });
        contactId = resolved.id;
      } else {
        throw new Error("Pick someone to message first.");
      }

      // 2. Route to the right backend path (AI voice only — operator goes via CallDialog).
      if (channel === "voice") {
        if (!firstPhone) throw new Error("No phone number available for this contact.");
        return telephonyApi.prepareOutboundCall({
          contactId,
          to: firstPhone,
          mode: "ai",
          ...(voice.rooftops[0] ? { rooftopId: voice.rooftops[0].id } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
      }

      const outboundChannel =
        channel === "sms"
          ? compatibleSmsChannels[0]
          : compatibleEmailChannels[0];
      if (targetLeadId && !outboundChannel) {
        throw new Error("No compatible SMS or email channel is available for this Lead.");
      }
      return bdcApi.startConversation(contactId, {
        channel: channel as "sms" | "email",
        ...(targetLeadId ? { leadId: targetLeadId } : {}),
        ...(targetLeadId && outboundChannel ? { channelId: outboundChannel.id } : {}),
        body: body.trim(),
        ...(channel === "email" && subject.trim() ? { subject: subject.trim() } : {}),
      });
    },
    onSuccess: async (result) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["bdc", "inbox"] }),
        qc.invalidateQueries({ queryKey: ["bdc", "leads"] }),
      ]);

      setOpen(false);
      reset();

      if (channel === "voice") {
        toast.success("Assistant call started");
        const r = result as { leadId: string };
        navigate(`/inbox/${r.leadId}`);
        return;
      }

      const r = result as { id?: string; leadId?: string };
      navigate(`/inbox/${r.leadId ?? r.id}`);
    },
    onError: (err) =>
      toast.error((err as Error).message ?? "Could not start the conversation."),
  });

  // For the operator voice path, we skip our own dialog send and delegate
  // to CallDialog (which handles softphone lifecycle).
  const handleSend = () => {
    if (channel === "voice" && voiceMode === "operator") {
      setOperatorCallOpen(true);
      setOpen(false);
      return;
    }
    send.mutate();
  };

  // Channel options visible for the current recipient.
  const availableChannels: Channel[] = (() => {
    if (!recipient) return [];
    if (recipient.kind === "contact") {
      return [
        recipient.contact.primaryPhone && hasSms ? "sms" : null,
        recipient.contact.primaryEmail && hasEmail ? "email" : null,
        !targetLeadId &&
        recipient.contact.primaryPhone &&
        hasVoice &&
        !recipient.contact.optoutVoice
          ? "voice"
          : null,
      ].filter(Boolean) as Channel[];
    }
    // "new" recipient
    return [
      recipient.channel === "sms" && hasSms ? "sms" : null,
      recipient.channel === "email" && hasEmail ? "email" : null,
      !targetLeadId && recipient.channel === "sms" && hasVoice ? "voice" : null,
    ].filter(Boolean) as Channel[];
  })();

  const canPickChannel = availableChannels.length > 1;

  // Snap the channel to one that's actually available for the current
  // recipient. The "sms" initializer only suits the search flow (which
  // re-snaps in chooseContact) — a preset email-only contact otherwise
  // stays stuck on SMS with the picker hidden, and Send routes an SMS
  // to a contact with no phone.
  useEffect(() => {
    const first = availableChannels[0];
    if (recipient && first !== undefined && !availableChannels.includes(channel)) {
      setChannel(first);
    }
  }, [recipient, availableChannels, channel]);

  // Operator mode is only actionable for an existing contact (the
  // CallDialog softphone requires one). If the recipient switches to a
  // typed-in new number while "operator" is selected, snap back to the
  // AI call — otherwise Send silently no-ops (#368).
  useEffect(() => {
    if (recipient?.kind !== "contact" && voiceMode === "operator") {
      setVoiceMode("ai");
    }
  }, [recipient, voiceMode]);

  const recipientLabel =
    recipient?.kind === "contact"
      ? contactDisplayName(recipient.contact)
      : recipient?.kind === "new"
        ? recipient.value
        : "";

  const sendDisabled =
    send.isPending ||
    recipient === null ||
    availableChannels.length === 0 ||
    (channel !== "voice" && body.trim().length === 0) ||
    (channel === "voice" && voiceMode !== "operator" && !firstPhone);

  const buttonLabel = "Start conversation";

  return (
    <>
      {trigger !== "none" && (
        <Button size="toolbar" onClick={() => setOpen(true)}>
          <PenSquare className="h-4 w-4" />
          {buttonLabel}
        </Button>
      )}

      {/* Operator call sub-dialog — rendered outside the compose dialog so
          CallDialog can manage its own open/close lifecycle. */}
      {recipient?.kind === "contact" ? (
        <CallDialog
          open={operatorCallOpen}
          onClose={() => setOperatorCallOpen(false)}
          contact={recipient.contact}
          phones={recipientPhones}
          initialTarget={firstPhone}
          onPickTarget={() => {}}
          rooftops={voice.rooftops}
        />
      ) : null}

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) reset();
        }}
      >
        <DialogContent className="sm:max-w-md" preventDismiss={send.isPending || body.trim().length > 0 || subject.trim().length > 0 || notes.trim().length > 0 || newFirstName.trim().length > 0 || newLastName.trim().length > 0}>
          <DialogHeader>
            <DialogTitle>Start conversation</DialogTitle>
          </DialogHeader>

          {/* Step 1: Recipient — skipped when presetContact */}
          {!presetContact && recipient === null ? (
            <div className="space-y-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, phone, or email…"
                  className="pl-8"
                  autoFocus
                  aria-label="Search for a recipient"
                />
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border">
                {debounced.length < 2 ? (
                  <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                    Type at least 2 characters to search your contacts.
                  </p>
                ) : results.isPending ? (
                  <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                    Searching…
                  </p>
                ) : (
                  <>
                    {(results.data?.items ?? []).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => chooseContact(c)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:bg-muted"
                      >
                        <span className="min-w-0 flex-1 truncate font-medium">
                          {contactDisplayName(c)}
                        </span>
                        <span className="shrink-0 truncate text-xs text-muted-foreground">
                          {c.primaryPhone ?? c.primaryEmail ?? ""}
                        </span>
                      </button>
                    ))}
                    {newTarget ? (
                      <button
                        type="button"
                        onClick={() => chooseNew(newTarget)}
                        className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:bg-muted"
                      >
                        <UserPlus className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">
                          Message new {newTarget.channel === "email" ? "email" : "number"}{" "}
                          <span className="font-medium">{newTarget.value}</span>
                        </span>
                      </button>
                    ) : null}
                    {(results.data?.items.length ?? 0) === 0 && !newTarget ? (
                      <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                        No matches. Search by name, or type a full email or phone (+1…).
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Recipient badge (hidden when preset — user already knows) */}
              {!presetContact ? (
                <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {recipientLabel}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setRecipient(null);
                      setBody("");
                      setSubject("");
                      setNotes("");
                      setNewFirstName("");
                      setNewLastName("");
                    }}
                    className="-my-2 shrink-0 rounded p-2.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:-my-0.5 sm:p-1"
                    aria-label="Change recipient"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : null}

              {/* Optional name fields for new contacts */}
              {recipient?.kind === "new" ? (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={newFirstName}
                    onChange={(e) => setNewFirstName(e.target.value)}
                    placeholder="First name (optional)"
                    maxLength={120}
                  />
                  <Input
                    value={newLastName}
                    onChange={(e) => setNewLastName(e.target.value)}
                    placeholder="Last name (optional)"
                    maxLength={120}
                  />
                </div>
              ) : null}

              {/* Channel picker */}
              {availableChannels.length === 0 ? (
                <p className="border-y py-3 text-sm text-muted-foreground">
                  {targetLeadId
                    ? "No SMS or email channel is available. Connect one or open the contact to place a call."
                    : "No conversation channel is available for this contact."}
                </p>
              ) : canPickChannel ? (
                <div className="flex gap-2">
                  {availableChannels.map((ch) => (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => setChannel(ch)}
                      className={cn(
                        "flex flex-1 min-w-0 flex-col items-center gap-1.5 rounded-md border px-2 py-2.5 text-xs font-semibold transition-colors",
                        channel === ch
                          ? "border-primary bg-accent text-primary"
                          : "border-input text-muted-foreground hover:bg-muted",
                      )}
                    >
                      <ChannelIcon channel={ch} size="sm" />
                      {ch === "voice" ? "Voice" : channelLabel(ch)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ChannelIcon channel={channel} size="xs" />
                  Sending via {channel === "voice" ? "Voice" : channelLabel(channel)}.
                </p>
              )}

              {/* Voice sub-choice: AI or operator. Operator calls need an
                  EXISTING contact — CallDialog (the softphone) is only
                  mounted for kind==="contact", so offering "Operator" to
                  a typed-in new number was a silent no-op (#368): the
                  dialog just closed and no call started. A new number
                  gets the AI call (which resolves/creates the contact
                  server-side); the operator can dial from the created
                  contact right after. */}
              {availableChannels.length === 0 ? null : channel === "voice" ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Call mode</p>
                  <div className="flex gap-2">
                    {(recipient?.kind === "contact"
                      ? (["ai", "operator"] as VoiceMode[])
                      : (["ai"] as VoiceMode[])
                    ).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setVoiceMode(mode)}
                        className={cn(
                          "flex flex-1 min-w-0 flex-col items-center gap-1.5 rounded-md border px-2 py-2.5 text-xs font-semibold transition-colors",
                          voiceMode === mode
                            ? "border-primary bg-accent text-primary"
                            : "border-input text-muted-foreground hover:bg-muted",
                        )}
                      >
                        {mode === "ai" ? (
                          <Sparkles className="h-4 w-4" />
                        ) : (
                          <ChannelIcon channel="voice" size="sm" />
                        )}
                        {mode === "ai" ? "Assistant" : "Operator"}
                      </button>
                    ))}
                  </div>
                  {voiceMode === "ai" ? (
                    <div className="space-y-1">
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Notes for the Assistant, e.g. confirm a 2pm Thursday test drive (optional)"
                        maxLength={500}
                        rows={2}
                      />
                      <p className="text-right text-xs text-muted-foreground">
                        {notes.length}/500
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  {channel === "email" ? (
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Subject (optional)"
                      maxLength={200}
                    />
                  ) : null}
                  <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={channel === "sms" ? "Write a text message…" : "Write an email…"}
                    rows={5}
                    autoFocus
                  />
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            {recipient !== null ? (
              <Button onClick={handleSend} disabled={sendDisabled}>
                {availableChannels.length === 0
                  ? "No channel available"
                  : send.isPending
                  ? channel === "voice"
                    ? "Calling…"
                    : "Sending…"
                  : channel === "voice"
                    ? voiceMode === "ai"
                      ? "Have Assistant call"
                      : "Call"
                    : "Send"}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
